import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../primitives/Sheet';
import { useResourceKit } from '@resource-kit/core/react';
import { resourceKeys } from '@resource-kit/controllers';
import { DEFAULT_STALE_TIME, useRowBusy } from '@resource-kit/controllers/react';
import type { AnyFieldDef } from '@resource-kit/model';
import { ResourceDetailPanel } from './ResourceDetailPanel';
import { invalidateAfterLeave } from './invalidateAfterLeave';
import type { ResourceBundle } from '@resource-kit/controllers/react';

export interface DetailTarget<T> {
  id: string;
  listItem: T;
}

export interface ResourceDetailSurfaceProps<T, F extends Record<string, AnyFieldDef>> {
  bundle: ResourceBundle<T, F>;
  columns: ColumnDef<T>[];
  /** The row the drawer describes; kept during the close animation. */
  target: DetailTarget<T> | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Full-page route for a row, used by the `both` surface's header link. */
  detailPath: (item: T) => string;
  canUpdate: boolean;
  canDelete: boolean;
  /** Called after a successful delete so the list can settle its paging. */
  onDeleted: () => void;
}

/**
 * The record surface behind a list row (#1801, extracted from ResourcePage).
 *
 * It owns three coupled things — the detail fetch, the drawer, and the edit /
 * delete affordances offered inside it — which share exactly one input
 * (the targeted row) and nothing at all with the list's query state. That is
 * the seam: the list controller decides WHICH rows exist, this decides what
 * happens when you open one.
 *
 * Requires a `RowBusyProvider` ancestor: the built-in delete claims the row
 * there, like the row action runner does (#2814).
 */
export function ResourceDetailSurface<T, F extends Record<string, AnyFieldDef>>({
  bundle,
  columns,
  target,
  open,
  onOpenChange,
  detailPath,
  canUpdate,
  canDelete,
  onDeleted,
}: ResourceDetailSurfaceProps<T, F>) {
  const { t, confirm, notify } = useResourceKit();
  const queryClient = useQueryClient();
  const busy = useRowBusy();

  const keys = resourceKeys(bundle.host.slug);
  const hasDetailApi = !!bundle.host.api.get;

  // Fetch the full record when a detail API exists, so fields excluded from
  // the list response (e.g. payload) are shown. The list item renders
  // immediately; detail data replaces it once loaded.
  const detailQuery = useQuery({
    queryKey: keys.detail(target?.id),
    queryFn: () => {
      if (!bundle.host.api.get || !target) {
        throw new Error(`Missing detail API or target for ${bundle.host.slug}`);
      }
      return bundle.host.api.get(target.id);
    },
    enabled: open && !!target && hasDetailApi,
    staleTime: bundle.host.staleTime ?? DEFAULT_STALE_TIME,
  });

  const item = (hasDetailApi ? detailQuery.data : null) ?? target?.listItem ?? null;
  const defLabel = t(bundle.host.label, { defaultValue: bundle.host.label });
  const surface = bundle.host.detail?.surface ?? 'both';
  const fullPageAction =
    surface === 'both' && target ? (
      <Link
        to={detailPath(target.listItem)}
        className="text-sm text-primary underline-offset-2 hover:underline"
      >
        {t('rk.resource.openFullPage')}
      </Link>
    ) : undefined;

  const handleDelete = async () => {
    if (!target || !bundle.host.api.delete) return;

    // The same shared claim the row action runner takes: a second activation
    // while the confirm or the request is pending is ignored, so a double
    // confirm cannot send a second DELETE that answers 404 (#2814).
    const rowId = target.id;
    if (!busy.begin(rowId)) return;
    try {
      const yes = await confirm({
        title: t('rk.resource.deleteConfirmTitle', { name: defLabel }),
        message: t('rk.resource.deleteConfirmMessage', { name: defLabel }),
        intent: 'danger',
        confirmText: t('rk.ui.delete'),
      });
      if (!yes) return;

      try {
        await bundle.host.api.delete(rowId);
        // The record is gone: refresh the list without reading its detail
        // again, which would answer 404 in the still-open drawer (#2802).
        void invalidateAfterLeave(queryClient, keys, rowId);
        notify(
          'success',
          t('rk.resource.deleted'),
          t('rk.resource.deletedSuccess', { name: defLabel }),
        );
        onDeleted();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : t('rk.error.unknown');
        notify('error', t('rk.resource.deleteFailed'), msg);
      }
    } finally {
      busy.end(rowId);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:w-1/3 sm:min-w-[800px] flex flex-col h-full overflow-hidden">
          <SheetHeader>
            <SheetTitle className="sr-only">
              {defLabel} {t('rk.resource.details')}
            </SheetTitle>
          </SheetHeader>
          {detailQuery.isError ? (
            <div className="mt-6 flex-1 flex items-center justify-center">
              <p className="text-sm text-destructive">
                {t('rk.ui.error')}: {detailQuery.error?.message}
              </p>
            </div>
          ) : item ? (
            <div className="mt-6 flex-1 overflow-y-auto min-h-0 pr-4">
              <ResourceDetailPanel
                item={item}
                bundle={bundle}
                columns={columns}
                embedded
                showEmbeddedHeader
                // Editing is an IN-PLACE mode switch owned by
                // the panel's `FormView` (#1797): a capability
                // flag, not a dialog opener. Nothing stacks a
                // Modal on top of this Sheet any more.
                canEdit={canUpdate}
                onDelete={canDelete ? handleDelete : undefined}
                deleteBusy={target ? busy.isBusy(target.id) : false}
                additionalActions={fullPageAction}
                // #1417: a `detail.actions` action with
                // `afterAction: 'close'` dismisses the drawer.
                onClose={() => onOpenChange(false)}
              />
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}
